import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, DefaultButton, Spinner } from '@fluentui/react';
import Select from 'react-select';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';
import SortableUnitList from '../Components/SortableUnitList';

const selectStyles = {
    container: base => ({ ...base, width: '100%', marginBottom: 10 }),
    control: base => ({ ...base, border: '1px solid #aaaaaa' }),
    menuList: base => ({ ...base, maxHeight: '300px', overflowY: 'auto' }),
};

const SortSignupUnitsPage = () => {
    const [selectedCobrandId, setSelectedCobrandId] = useState(null);
    const [units, setUnits] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [{ data: cobrandsData }] = useAxios('/internal/cobrands?bustCache=true');

    const [{ data: unitsData, loading: loadingUnits }, getUnits] = useAxios(
        { method: 'GET' },
        { manual: true }
    );

    const [{ loading: saving }, saveOrder] = useAxios(
        { url: '/clients/schools/signup-order', method: 'PUT' },
        { manual: true }
    );

    const cobrandsOptions = cobrandsData?.map(c => ({ value: c.cobrandId, label: c.name })) || [];

    useEffect(() => {
        if (selectedCobrandId) {
            getUnits({ url: `/clients/schools/signup-order/${selectedCobrandId}` });
        }
    }, [selectedCobrandId]);

    useEffect(() => {
        setUnits(unitsData || []);
        setIsDirty(false);
    }, [unitsData]);

    const handleReorder = (reordered) => {
        setUnits(reordered);
        setIsDirty(true);
    };

    const handleSave = async () => {
        setErrorMessage('');

        try {
            const result = await saveOrder({
                data: {
                    cobrandId: selectedCobrandId,
                    orderedClientSchoolIds: units.map(u => u.clientSchoolID),
                },
            });

            if (result.isAxiosError) {
                setErrorMessage('Failed to save the unit order.');
                return;
            }

            setIsDirty(false);
            setShowToast(true);
        } catch (error) {
            setErrorMessage(error.message || 'An error occurred while saving.');
        }
    };

    const handleReset = () => {
        setUnits(unitsData || []);
        setIsDirty(false);
    };

    return (
        <div style={{ maxWidth: '600px', marginBottom: 500 }}>
            <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                <h4>Sort Signup Units</h4>
            </div>

            {errorMessage && (
                <div style={{ color: 'rgb(164, 38, 44)', marginBottom: 10 }}>{errorMessage}</div>
            )}

            <div className="panel-h" style={{ fontWeight: '500', marginBottom: 5 }}>Cobrand</div>
            <Select
                options={cobrandsOptions}
                placeholder="Select Cobrand"
                value={cobrandsOptions.find(option => option.value === selectedCobrandId) || null}
                onChange={(option) => setSelectedCobrandId(option.value)}
                isSearchable
                styles={selectStyles}
            />

            {loadingUnits && <Spinner label="Loading units..." styles={{ root: { marginTop: 20 } }} />}

            {!loadingUnits && selectedCobrandId && units.length === 0 && (
                <div style={{ marginTop: 20, color: '#666' }}>
                    This cobrand has no units marked "Display On Signup".
                </div>
            )}

            {!loadingUnits && units.length > 0 && (
                <>
                    <div style={{ marginTop: 20, marginBottom: 8, color: '#666' }}>
                        Drag units into the order they should appear in the signup dropdown.
                    </div>
                    <SortableUnitList units={units} onReorder={handleReorder} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 15 }}>
                        <PrimaryButton
                            text="Save Order"
                            onClick={handleSave}
                            disabled={!isDirty || saving}
                        />
                        {isDirty && (
                            <DefaultButton
                                text="Reset"
                                onClick={handleReset}
                                disabled={saving}
                            />
                        )}
                    </div>
                </>
            )}

            <Toast show={showToast} message="Order saved!" onClose={() => setShowToast(false)} />
            <FeatureSummary
                label="About Signup Unit Order"
                summary="Controls the order units appear in the campus/affiliation dropdown on the signup page for a cobrand. Only units marked 'Display On Signup' are listed here. Saving sets an explicit order for every unit shown; if you have never sorted a cobrand, its units fall back to alphabetical order with 'Other'-style options last."
            />
        </div>
    );
};

export default SortSignupUnitsPage;
